import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { TabsContent } from '@/components/ui/tabs';
import { MessageSquare, Edit, Trash2, Plus, ChevronDown, ChevronUp } from 'lucide-react';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
  const [year, month, day] = datePart.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

export const CommunicationsTab = ({ 
  communications, 
  canEditLLC, 
  onAddCommunication, 
  onEditCommunication, 
  onDeleteCommunication, 
  onViewCommunication 
}) => {
  const [expandedMobileComm, setExpandedMobileComm] = useState(null);

  return (
    <TabsContent value="communications" className="mt-4">
      <Card className="border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Communications Log</CardTitle>
            <CardDescription>
              {communications.length} entr{communications.length !== 1 ? 'ies' : 'y'} logged
            </CardDescription>
          </div>
          {canEditLLC && (
            <Button onClick={onAddCommunication} className="bg-slate-900 hover:bg-slate-800">
              <Plus className="w-4 h-4 mr-2" />
              Add Entry
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {communications.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No communications logged yet</p>
            </div>
          ) : (
            <>
              {/* Mobile Card View - visible on small screens only */}
              <div className="md:hidden">
                {/* Mobile Header */}
                <div className="bg-slate-800 rounded-t-lg px-3 py-2.5">
                  <div className="grid grid-cols-12 gap-1 text-white text-xs font-semibold">
                    <div className="col-span-4">Date</div>
                    <div className="col-span-8">To / About</div>
                  </div>
                </div>
                {/* Mobile Rows */}
                <div className="border border-t-0 border-slate-200 rounded-b-lg divide-y divide-slate-100">
                  {communications.map((comm) => {
                    const isExpanded = expandedMobileComm === comm.id;
                    
                    return (
                      <div key={comm.id}>
                        {/* Collapsed Row */}
                        <div 
                          className="px-3 py-2.5 cursor-pointer hover:bg-slate-50 transition-colors"
                          onClick={() => setExpandedMobileComm(isExpanded ? null : comm.id)}
                          data-testid={`mobile-comm-row-${comm.id}`}
                        >
                          <div className="grid grid-cols-12 gap-1 items-center">
                            <div className="col-span-4 text-sm text-slate-700">
                              {formatDate(comm.date)}
                            </div>
                            <div className="col-span-7 min-w-0">
                              <p className="text-sm font-medium text-slate-900 truncate">{comm.to_party}</p>
                              <p className="text-xs text-slate-500 truncate">{comm.about}</p>
                            </div>
                            <div className="col-span-1 flex justify-end">
                              {isExpanded ? (
                                <ChevronUp className="w-4 h-4 text-slate-400" />
                              ) : (
                                <ChevronDown className="w-4 h-4 text-slate-400" />
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Expanded Details */}
                        {isExpanded && (
                          <div className="px-3 pb-3 pt-1 bg-slate-50/50 border-t border-slate-100">
                            <div className="space-y-2 text-sm">
                              {comm.notes && (
                                <div>
                                  <span className="text-slate-500 text-xs">Notes</span>
                                  <p className="text-slate-700">{comm.notes}</p>
                                </div>
                              )}
                              <div className="flex justify-between text-xs">
                                <span className="text-slate-500">Posted by: {comm.created_by_name}</span>
                              </div>
                              <div className="flex gap-2 pt-2 border-t border-slate-100">
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="flex-1"
                                  onClick={(e) => { e.stopPropagation(); onViewCommunication(comm); }}
                                >
                                  View Details
                                </Button>
                                {canEditLLC && (
                                  <>
                                    <Button 
                                      variant="outline" 
                                      size="sm"
                                      onClick={(e) => { e.stopPropagation(); onEditCommunication(comm); }}
                                    >
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                    <Button 
                                      variant="outline" 
                                      size="sm" 
                                      className="text-rose-600"
                                      onClick={(e) => { e.stopPropagation(); onDeleteCommunication(comm.id); }}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Desktop Table View - hidden on mobile */}
              <div className="hidden md:block border border-slate-200 rounded-lg overflow-x-auto">
              <div>
                <table className="w-full text-sm table-fixed">
                  <thead className="bg-slate-800 sticky top-0 z-10">
                    <tr>
                      <th className="text-left text-white font-semibold px-3 py-2.5 w-[85px]">Date</th>
                      <th className="text-left text-white font-semibold px-3 py-2.5 w-[15%]">To</th>
                      <th className="text-left text-white font-semibold px-3 py-2.5 w-[25%]">About</th>
                      <th className="text-left text-white font-semibold px-3 py-2.5">Notes</th>
                      <th className="text-left text-white font-semibold px-3 py-2.5 w-[140px]">Posted By</th>
                      {canEditLLC && <th className="text-left text-white font-semibold px-3 py-2.5 w-[80px]">Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {communications.map(comm => (
                      <tr 
                        key={comm.id} 
                        className="hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-b-0"
                        onClick={() => onViewCommunication(comm)}
                      >
                        <td className="text-slate-700 py-2 px-3 whitespace-nowrap text-xs">{formatDate(comm.date)}</td>
                        <td className="text-slate-700 py-2 px-3 truncate">{comm.to_party}</td>
                        <td className="text-slate-900 font-medium py-2 px-3 truncate">{comm.about}</td>
                        <td className="text-slate-600 py-2 px-3 truncate text-xs">{comm.notes || '-'}</td>
                        <td className="text-slate-500 py-2 px-3 text-xs whitespace-nowrap">{comm.created_by_name}</td>
                        {canEditLLC && (
                          <td className="py-2 px-2">
                            <div className="flex gap-0" onClick={(e) => e.stopPropagation()}>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onEditCommunication(comm)}>
                                <Edit className="w-3.5 h-3.5" />
                              </Button>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-rose-600" onClick={() => onDeleteCommunication(comm.id)}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            </>
          )}
        </CardContent>
      </Card>
    </TabsContent>
  );
};
